import { CheckmarkFilled, CloseFilled } from "@carbon/icons-react";
import { FC } from "react";

import { RelatedCasesTable } from "src/components/case/RelatedCasesTable";
import DynamicViewSchemaProvider from "src/dynamic-view-renderer/DynamicViewSchemaProvider/DynamicViewSchemaProvider";
import {
  BasicWidgetHorizontalProperty,
  BasicWidgetHorizontalPropertyLayout,
} from "src/dynamic-view-renderer/Widgets/BasicWidget/BasicWidgetHorizontal";
import WidgetContainer, {
  WidgetContainerProps,
} from "src/dynamic-view-renderer/Widgets/WidgetContainer";
import { useT } from "src/internationalization";
import { useFormatDate } from "src/internationalization/formatters";
import { Case } from "src/types/case";
import { Rule } from "src/types/rule";

export type ExecutionResultProps = {
  executionTime: Date | null;
  rule: Rule;
  matched: boolean;
  relatedCases: Case[];
  caseTypeCode: string;
} & Omit<WidgetContainerProps, "children">;

export const ExecutionResult: FC<ExecutionResultProps> = ({
  caseTypeCode,
  executionTime,
  rule,
  matched,
  relatedCases,
  ...containerProps
}) => {
  const t = useT();
  const formatDate = useFormatDate();

  const properties = [
    {
      label: t("page.cases.executeRule.modal.step2.resultStatus.label"),
      value: (
        <div className="flex items-center gap-2 text-sm">
          {matched ? (
            <>
              <CheckmarkFilled size={16} className="text-supportSuccess" />
              <span>{t("page.cases.executeRule.modal.step2.resultStatus.positive")}</span>
            </>
          ) : (
            <>
              <CloseFilled size={16} className="text-supportError" />
              <span>{t("page.cases.executeRule.modal.step2.resultStatus.negative")}</span>
            </>
          )}
        </div>
      ),
    },
    {
      label: t("page.cases.executeRule.modal.step2.executionTime.label"),
      value: executionTime
        ? formatDate(executionTime, { dateStyle: "medium", timeStyle: "short" })
        : "",
    },
    {
      label: t("page.cases.executeRule.modal.step2.executedRule.label"),
      value: rule.name,
    },
  ];

  return (
    <div className="flex flex-col gap-6 py-5 h-full min-h-0">
      {/* Execution Report Tile */}
      <div className="flex-shrink-0">
        <WidgetContainer {...containerProps}>
          <div className="flex flex-col gap-5">
            <h4 className="text-heading-04">
              {t("page.cases.executeRule.modal.step2.executionReport.title")}
            </h4>
            <BasicWidgetHorizontalPropertyLayout>
              {properties.map((property, index) => (
                <BasicWidgetHorizontalProperty
                  key={index}
                  label={property.label}
                  value={property.value}
                />
              ))}
            </BasicWidgetHorizontalPropertyLayout>
          </div>
        </WidgetContainer>
      </div>

      {/* Related Cases Section */}
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        <DynamicViewSchemaProvider>
          <RelatedCasesTable
            caseTypeCode={caseTypeCode}
            cases={relatedCases}
            loading={false}
            isError={false}
            fullHeight={false}
          />
        </DynamicViewSchemaProvider>
      </div>
    </div>
  );
};
